import { type ReactNode } from "react";
import {
  AllCommunityModule,
  provideGlobalGridOptions,
  themeQuartz,
} from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";

import "./DataGrid.css";

export const DATA_GRID_ROW_HEIGHT = 52;
export const DATA_GRID_HEADER_HEIGHT = 40;

/**
 * References an app design token (a tw-colors `--twc-*` custom property) as an
 * AG Grid theme colour. AG Grid params take literal CSS values rather than
 * Tailwind classes, so — like every chart in the app — we hand it the token's
 * CSS variable directly: the value already carries the correct per-theme colour
 * and flips with the `.dark` / `.light` class on the document root, so the grid
 * follows light/dark with no re-render (same mechanism as `accentColor`).
 *
 * Token names contain a literal `/` (e.g. `background/primary`), which must be
 * escaped inside `var()` — an unescaped slash makes the value silently resolve
 * to `transparent`. This helper encapsulates that escaping.
 */
export const token = (name: string) =>
  `hsl(var(--twc-${name.replaceAll("/", "\\/")}))`;

provideGlobalGridOptions({
  defaultColDef: {
    sortable: true,
    resizable: true,
    suppressMovable: true,
  },
  tooltipShowDelay: 0,
  // Colours are driven by the app's design tokens so the grid tracks light/dark
  // like the rest of the UI. AG Grid derives every other surface (rows, hover,
  // inputs, icons, scrollbars) from this small base set via CSS `color-mix`, so
  // these few params theme the whole grid. `browserColorScheme: "inherit"` lets
  // native controls/scrollbars follow the root `color-scheme`.
  theme: themeQuartz.withParams({
    rowHeight: DATA_GRID_ROW_HEIGHT,
    headerHeight: DATA_GRID_HEADER_HEIGHT,
    fontFamily: "inherit",
    fontSize: 12,
    iconSize: 12,
    cellHorizontalPaddingScale: 0.8,
    accentColor: token("brand-default"),
    backgroundColor: token("background/primary"),
    foregroundColor: token("text/primary"),
    borderColor: token("border/primary"),
    chromeBackgroundColor: token("background/subtle"),
    browserColorScheme: "inherit",
  }),
});

const agGridModules = [AllCommunityModule];

export const DataGridProvider = ({ children }: { children: ReactNode }) => (
  <AgGridProvider modules={agGridModules}>{children}</AgGridProvider>
);
